import { Button, message, Typography } from "antd";
import React, { useState, useRef, useEffect } from "react";
import { useUserFilesTabs } from "./UserFilesTabsContext";
import "./ConselorTakeAway.scss";
import { articlesSVC } from "../../../services";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { AppModal, AppUpload } from "../../../components/common";

const { Text } = Typography;

type Option = { label: string; value: string; image?: string };

const IMMERSIVE_CARE_OPTIONS: Option[] = [
  { label: "Crisis Care", value: "Crisis Care" },
  { label: "Quick-Fix", value: "Quick-Fix" },
  { label: "Daily Care", value: "Daily Care" },
  { label: "Daily Affirmations", value: "Daily Affirmations" },
  { label: "Be Healthy", value: "Be Healthy" },
  { label: "Be Happy", value: "Be Happy" },
  { label: "Be Fearless", value: "Be Fearless" },
  { label: "Be Creative", value: "Be Creative" },
  { label: "Sleep Easy", value: "Sleep Easy" },
  { label: "Be Social", value: "Be Social" },
];

const LIVE_WELLNESS_OPTIONS: Option[] = [
  { label: "Wellness Chat", value: "Wellness Chat" },
  { label: "Wellness Counseling", value: "Wellness Counseling" },
];

const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "indent",
];

function HashtagSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Option[];
  value: string[];
  onChange: (vals: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val: string) =>
    onChange(
      value.includes(val) ? value.filter((v) => v !== val) : [...value, val],
    );

  const selectedLabels = options.filter((o) => value.includes(o.value));

  return (
    <div className="takeaway-field" ref={ref}>
      <p className="takeaway-field__label">{label}</p>
      <div
        className={`takeaway-select ${open ? "takeaway-select--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <div className="takeaway-select__inner">
          {selectedLabels.length === 0 ? (
            <span className="takeaway-select__placeholder">
              Choose an option...
            </span>
          ) : (
            <div className="takeaway-select__tags">
              {selectedLabels.map((o) => (
                <span key={o.value} className="takeaway-select__tag">
                  {o.label}
                  <button
                    className="takeaway-select__tag-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(o.value);
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <span
            className={`takeaway-select__arrow ${open ? "takeaway-select__arrow--up" : ""}`}
          >
            ▾
          </span>
        </div>
        {open && (
          <div
            className="takeaway-select__dropdown"
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((o) => (
              <div
                key={o.value}
                className={`takeaway-select__option ${value.includes(o.value) ? "takeaway-select__option--selected" : ""}`}
                onClick={() => toggle(o.value)}
              >
                {o.image && (
                  <img
                    src={o.image}
                    alt={o.label}
                    className="takeaway-select__option-img"
                  />
                )}
                <span>{o.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldInput({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="takeaway-field">
      <p className="takeaway-field__label">{label}</p>
      <input
        className="takeaway-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function FilePicker({
  label,
  file,
  onChange,
  existingUrl,
}: {
  label: string;
  file: File | null;
  onChange: (f: File | null) => void;
  existingUrl?: string;
}) {
  const previewUrl = file ? URL.createObjectURL(file) : (existingUrl ?? null);

  return (
    <div className="takeaway-field">
      <p className="takeaway-field__label">{label}</p>
      <AppUpload
        kind="image"
        uploadedImage={previewUrl}
        handleRemove={previewUrl ? () => onChange(null) : undefined}
        handleFileUpload={(e) => {
          const selected = e.target.files?.[0] ?? null;
          if (selected && selected.size > 10 * 1024 * 1024) {
            message.error("Image size should not exceed 10MB");
            e.target.value = "";
            return;
          }
          onChange(selected);
        }}
      />
    </div>
  );
}

interface ArticleEntry {
  articleUrl: string;
  articleTitle: string;
  articleDescription: string;
  thumbnail: File | null;
  thumbnailUrl?: string;
}

const EMPTY_ARTICLE: ArticleEntry = {
  articleUrl: "",
  articleTitle: "",
  articleDescription: "",
  thumbnail: null,
  thumbnailUrl: "",
};

function ArticleCard({
  article,
  onChange,
}: {
  article: ArticleEntry;
  onChange: (a: ArticleEntry) => void;
}) {
  return (
    <div className="takeaway-article-card take_away_alt">
      <FieldInput
        label="Article URL"
        placeholder="Paste the article URL here"
        value={article.articleUrl}
        onChange={(v) => onChange({ ...article, articleUrl: v })}
      />
      <FieldInput
        label="Article Title"
        placeholder="Enter the article title"
        value={article.articleTitle}
        onChange={(v) => onChange({ ...article, articleTitle: v })}
      />
      <FieldInput
        label="Article Description"
        placeholder="Write a brief description"
        value={article.articleDescription}
        onChange={(v) => onChange({ ...article, articleDescription: v })}
      />
      <FilePicker
        label="Thumbnail Image"
        file={article.thumbnail}
        onChange={(f) => onChange({ ...article, thumbnail: f })}
        existingUrl={article.thumbnailUrl}
      />
    </div>
  );
}

export default function ConselorTakeAway() {
  const {
    setCounselorTakeAwayModal,
    selectedModalObjCounselor,
    counselorTakeAwayModal,
    setTakeAwayObj,
    TakeAwayObj,
  } = useUserFilesTabs();

  const [immersiveCare, setImmersiveCare] = useState<string[]>([]);
  const [liveWellness, setLiveWellness] = useState<string[]>([]);
  const [themesSoulsara, setThemesSoulsara] = useState<string[]>([]);
  const [soulsaraTimes, setSoulsaraTimes] = useState<string[]>([]);
  const [eventVideos, setEventVideos] = useState<string[]>([]);
  const [articles, setArticles] = useState<ArticleEntry[]>([
    { ...EMPTY_ARTICLE },
  ]);
  const [reportSummary, setReportSummary] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [themesOptions, setThemesOptions] = useState<Option[]>([]);
  const [timesOptions, setTimesOptions] = useState<Option[]>([]);
  const [videosOptions, setVideosOptions] = useState<Option[]>([]);

  useEffect(() => {
    const toOptions = (data: any[]) =>
      data?.map((i) => ({
        label: i.title,
        value: i._id,
        image: i.thumbnailImage ?? i.image ?? "",
      })) ?? [];
    articlesSVC
      .getThemeposts()
      .then((res: any) => setThemesOptions(toOptions(res?.data)));
    articlesSVC
      .getTimesList()
      .then((res: any) => setTimesOptions(toOptions(res?.data)));
    articlesSVC
      .getVideoList()
      .then((res: any) => setVideosOptions(toOptions(res?.data?.data)));
  }, []);

  // ─── Populate form from existing data ────────────────────────────────────
  useEffect(() => {
    const obj = selectedModalObjCounselor;

    if (!obj || Object.keys(obj).length === 0) {
      setImmersiveCare([]);
      setLiveWellness([]);
      setThemesSoulsara([]);
      setSoulsaraTimes([]);
      setEventVideos([]);
      setArticles([{ ...EMPTY_ARTICLE }]);
      setReportSummary("");
      return;
    }

    setImmersiveCare(obj.immersiveCare ?? []);
    setLiveWellness(obj.liveWellnessSupport ?? []);
    setThemesSoulsara(obj.themes?.map((t: any) => t) ?? []);
    setSoulsaraTimes(obj.times?.map((t: any) => t) ?? []);
    setEventVideos(
      obj.eventVideos?.map((v: any) => {
        if (typeof v === "string") return v;

        return Object.keys(v)
          .filter((k) => !isNaN(Number(k)))
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => v[k])
          .join("");
      }) ?? [],
    );
    setArticles(
      obj.externalArticles?.length
        ? obj.externalArticles.map((a: any) => ({
            articleUrl: a.articleUrl ?? "",
            articleTitle: a.articleTitle ?? "",
            articleDescription: a.articleDescription ?? "",
            thumbnail: null,
            thumbnailUrl: a.thumbnailImage ?? "", // ← add this
          }))
        : [{ ...EMPTY_ARTICLE }],
    );
    setReportSummary(obj.reportSummary ?? "");
  }, [selectedModalObjCounselor, counselorTakeAwayModal]);
  console.log(eventVideos, soulsaraTimes, "udhayakumar");

  const buildPayload = () => {
    const formData = new FormData();

    immersiveCare.forEach((item) => {
      formData.append("immersiveCare[]", item);
    });

    liveWellness.forEach((item) => {
      formData.append("liveWellnessSupport[]", item);
    });

    // THEMES
    themesSoulsara.forEach((themeId) => {
      formData.append("themes[]", themeId);
    });

    // TIMES
    soulsaraTimes.forEach((timeId) => {
      formData.append("times[]", timeId);
    });

    // EVENT VIDEOS
    eventVideos.forEach((videoId, index) => {
      formData.append(`eventVideos[${index}]`, videoId);
    });

    formData.append("reportSummary", reportSummary);

    const filteredArticles = articles.filter(
      (a) => a.articleUrl || a.articleTitle,
    );

    filteredArticles.forEach((article, index) => {
      formData.append(
        `externalArticles[${index}][articleTitle]`,
        article.articleTitle,
      );
      formData.append(
        `externalArticles[${index}][articleUrl]`,
        article.articleUrl,
      );
      formData.append(
        `externalArticles[${index}][articleDescription]`,
        article.articleDescription,
      );
      formData.append(
        `externalArticles[${index}][thumbnailImage]`,
        article.thumbnailUrl ?? "",
      );

      if (article.thumbnail) {
        formData.append(`externalThumbnail_${index}`, article.thumbnail);
      }
    });

    return formData;
  };

  const handleSubmit = async () => {
    console.log(buildPayload(), "submit");

    // return false
    setSubmitting(true);
    articlesSVC
      .addTakeway(TakeAwayObj?.sessionId, buildPayload())
      .then(() => {
        setCounselorTakeAwayModal(false);
        message.success("Takeaway submitted successfully");
     })
      .catch(() => {
        message.warning("Takeaway already submitted for this session");
      })
      .finally(() => setSubmitting(false));
  };

  const hasContent =
    immersiveCare.length > 0 ||
    liveWellness.length > 0 ||
    themesSoulsara.length > 0 ||
    soulsaraTimes.length > 0 ||
    articles.some((a) => a.articleUrl || a.articleTitle);

  const isPending = TakeAwayObj?.approvalStatus === "rejected";
  console.log(isPending, "isPending", TakeAwayObj);

  return (
    <AppModal
      className="takeawayModal"
      title="Add Takeaway Resources"
      footer={
        <Button
          type="primary"
          loading={submitting}
          disabled={!hasContent || submitting}
          onClick={handleSubmit}
        >
          Submit for Review
        </Button>
      }
      open={counselorTakeAwayModal}
      onCancel={() => setCounselorTakeAwayModal(false)}
    >
      {isPending && (
        <div className="rej_pop">
          <svg
            width="57"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.003 16H12.011M12.003 9.99997V13M10.578 5.21697L3.51998 17C3.37456 17.252 3.29765 17.5377 3.29688 17.8287C3.29612 18.1197 3.37153 18.4058 3.51562 18.6586C3.65972 18.9114 3.86747 19.1221 4.11823 19.2697C4.36899 19.4173 4.65402 19.4967 4.94498 19.5H19.061C19.3521 19.497 19.6373 19.4178 19.8882 19.2702C20.1391 19.1227 20.347 18.912 20.4912 18.6591C20.6353 18.4062 20.7107 18.1199 20.7098 17.8288C20.7089 17.5377 20.6317 17.252 20.486 17L13.429 5.21697C13.2805 4.9719 13.0714 4.76925 12.8218 4.6286C12.5722 4.48796 12.2905 4.41406 12.004 4.41406C11.7175 4.41406 11.4358 4.48796 11.1862 4.6286C10.9365 4.76925 10.7274 4.9719 10.579 5.21697"
              stroke="#FF0000"
              strokeWidth="2"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p>
            Submission not approved. Please update and resubmit, and contact the
            Senior Counselor as required.
          </p>
        </div>
      )}

      <p className="takeaway-section-label">Our Solutions</p>
      <div className="takeaway-section-box">
        <HashtagSelect
          label="ImmersiveCARE"
          options={IMMERSIVE_CARE_OPTIONS}
          value={immersiveCare}
          onChange={setImmersiveCare}
        />
        <HashtagSelect
          label="LIVE Wellness Support"
          options={LIVE_WELLNESS_OPTIONS}
          value={liveWellness}
          onChange={setLiveWellness}
        />
      </div>

      <div className="takeaway-divider" />

      <p className="takeaway-section-label">Internal Articles</p>
      <div className="takeaway-section-box">
        <HashtagSelect
          label="THEMES@SOULSARA"
          options={themesOptions}
          value={themesSoulsara}
          onChange={setThemesSoulsara}
        />
        <HashtagSelect
          label="SOULSARA Times"
          options={timesOptions}
          value={soulsaraTimes}
          onChange={setSoulsaraTimes}
        />
      </div>

      <div className="takeaway-divider" />

      <p className="takeaway-section-label">External Articles</p>
      {articles.map((article, i) => (
        <ArticleCard
          key={i}
          article={article}
          onChange={(updated) =>
            setArticles((prev) =>
              prev.map((a, idx) => (idx === i ? updated : a)),
            )
          }
        />
      ))}
      <div className="takeaway-add-another">
        <button
          className="takeaway-add-btn"
          onClick={() => setArticles((prev) => [...prev, { ...EMPTY_ARTICLE }])}
        >
          Add Another Article
        </button>
      </div>

      <div className="takeaway-divider" />

      <p className="takeaway-section-label">Our Event Videos</p>
      <div className="takeaway-article-card">
        <HashtagSelect
          label="Event Video(s)"
          options={videosOptions}
          value={eventVideos}
          onChange={setEventVideos}
        />
      </div>

      <div className="takeaway-divider" />

      <p className="takeaway-section-label">Additional Comments</p>
      <div className="takeaway-article-card">
        <div className="takeaway-field react_quill">
          <ReactQuill
            theme="snow"
            value={reportSummary}
            onChange={setReportSummary}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Type your additional comments here..."
            className="takeaway-quill"
          />
        </div>
      </div>

      <div className="noteBox">
        <Text className="noteTitle">Note:</Text>
        <Text className="noteText">
          Once submitted, the <strong>Senior Counselor</strong> will review and
          approve the file. Once approved, the file will be shared directly with
          the user!
        </Text>
      </div>

    </AppModal>
  );
}
